/**
 * NovoETPPage — formulário de criação do ETP (F6a).
 *
 * Porte do new_etp.html (1.618 linhas). O que o formulário coleta são os
 * INSUMOS: a IA desenvolve cada seção a partir deles e do DFD. Por isso os
 * campos herdados da demanda (contratação direta, sigilo, credenciamento)
 * aparecem como leitura — mudá-los aqui contradiria o DFD aprovado.
 *
 * A geração segue no endpoint legado (POST /etp/create): são as 200 linhas
 * que montam sections_data e disparam a task. O formulário é que muda.
 */

import {
  faArrowLeft,
  faBan,
  faCheckCircle,
  faExclamationTriangle,
  faFileContract,
  faGavel,
  faInfoCircle,
  faLightbulb,
  faMagic,
  faMagnifyingGlassDollar,
  faSave,
  faSpinner,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router'
import {
  configuracaoETP,
  novoETP,
  salvarRascunhoETP,
  sugerirCampoETP,
} from '../../api/etp'
import { useToast } from '../../components/Toast'

const ATRASO_AUTOSAVE_MS = 2000

const AJUDA: Record<string, string> = {
  necessidade_contratacao: 'Descreva a necessidade da contratação...',
  demonstracao_pca: 'Demonstre o alinhamento com o PCA...',
  requisitos_contratacao: 'Descreva os requisitos da contratação...',
  estimativa_quantidades: 'Estime as quantidades necessárias...',
  levantamento_mercado: 'Apresente o levantamento de mercado...',
  estimativa_valor: 'Estime o valor da contratação...',
  descricao_solucao: 'Descreva a solução proposta...',
  justificativa_parcelamento: 'Justifique o parcelamento...',
  demonstrativo_resultados: 'Demonstre os resultados esperados...',
  providencias_previas: 'Descreva as providências prévias...',
  contratacoes_correlatas: 'Informe contratações correlatas...',
  impactos_ambientais: 'Descreva possíveis impactos ambientais (se aplicável)...',
  posicionamento_conclusivo: 'Apresente o posicionamento conclusivo...',
  analise_problematica: 'Descreva a problemática enfrentada pela Administração...',
  possiveis_solucoes: 'Descreva as possíveis soluções existentes no mercado...',
  escolha_solucao: 'Indique a solução mais adequada e justifique a escolha...',
}

// A seção do mapa de risco é gerada pela IA; o formulário não a coleta
const NAO_COLETADAS = new Set(['mapa_risco'])

const ROTULOS_HERDADOS: Record<string, string> = {
  tipo_contratacao_direta: 'Tipo de contratação',
  orcamento_sigiloso: 'Caráter do orçamento',
  hipotese_inexigibilidade: 'Hipótese de inexigibilidade',
  hipotese_dispensa: 'Hipótese de dispensa',
  inciso_dispensa: 'Inciso da dispensa',
  hipotese_credenciamento: 'Hipótese de credenciamento (Art. 79)',
  forma_distribuicao_demanda: 'Distribuição da demanda',
  origem_tabela_precos: 'Origem da tabela de preços',
  vigencia_credenciamento: 'Vigência do credenciamento',
}

export function NovoETPPage() {
  const { id } = useParams()
  const idNumerico = Number(id)
  const { notificar } = useToast()
  const [valores, setValores] = useState<Record<string, string>>({
    fonte_pesquisa_precos: 'api',
    webscraper_mode: 'auto',
  })
  const [sugerindo, setSugerindo] = useState<string | null>(null)
  const [sugestoesAtivas, setSugestoesAtivas] = useState(true)
  const [erros, setErros] = useState<Record<string, string>>({})
  const temporizador = useRef<number | null>(null)
  // um campo é auto-sugerido UMA vez (o botão continua valendo para refazer)
  const jaSugeridos = useRef<Set<string>>(new Set())
  const cancelados = useRef<Set<string>>(new Set())

  const { data: inicial, isLoading, error } = useQuery({
    queryKey: ['etp-novo', idNumerico],
    queryFn: () => novoETP(idNumerico),
    enabled: Number.isFinite(idNumerico),
    retry: false,
  })
  const { data: configuracao } = useQuery({
    queryKey: ['etp-configuracao'],
    queryFn: configuracaoETP,
    staleTime: Infinity,
  })

  useEffect(() => {
    if (!inicial) return
    setValores((atuais) => ({
      ...atuais,
      objeto_contratacao: inicial.objeto,
      ...inicial.rascunho,
    }))
  }, [inicial])

  const rascunho = useMutation({
    mutationFn: (dados: Record<string, string>) =>
      salvarRascunhoETP(idNumerico, dados),
  })

  const definir = useCallback(
    (campo: string, valor: string) => {
      setValores((atuais) => {
        const proximos = { ...atuais, [campo]: valor }
        if (temporizador.current) window.clearTimeout(temporizador.current)
        temporizador.current = window.setTimeout(
          () => rascunho.mutate(proximos),
          ATRASO_AUTOSAVE_MS,
        )
        return proximos
      })
      setErros((atuais) => {
        if (!atuais[campo]) return atuais
        const proximos = { ...atuais }
        delete proximos[campo]
        return proximos
      })
    },
    [rascunho],
  )

  useEffect(
    () => () => {
      if (temporizador.current) window.clearTimeout(temporizador.current)
    },
    [],
  )

  async function sugerir(campo: string, silencioso = false) {
    const objeto = valores.objeto_contratacao?.trim()
    if (!objeto) {
      if (!silencioso) {
        notificar('error', 'Informe o objeto da contratação antes de pedir sugestões.')
      }
      return
    }
    cancelados.current.delete(campo)
    setSugerindo(campo)
    try {
      const { sugestao } = await sugerirCampoETP(campo, objeto, idNumerico)
      // ESC durante a espera descarta o resultado (comportamento do original)
      if (cancelados.current.has(campo)) return
      definir(campo, sugestao)
    } catch (e) {
      if (!silencioso) notificar('error', (e as Error).message)
    } finally {
      setSugerindo(null)
    }
  }

  /** Sugestão ao FOCAR um campo vazio — é assim que o formulário original
   *  se comporta: o usuário clica no campo e a LicitIA preenche. O botão
   *  existe para refazer, não é o caminho principal. */
  function aoFocar(campo: string) {
    if (!sugestoesAtivas) return
    if (valores[campo]?.trim()) return
    if (jaSugeridos.current.has(campo)) return
    jaSugeridos.current.add(campo)
    void sugerir(campo, true)
  }

  /** ESC cancela a sugestão em andamento e limpa o campo. */
  function aoTeclar(campo: string, tecla: string) {
    if (tecla !== 'Escape') return
    cancelados.current.add(campo)
    setSugerindo((atual) => (atual === campo ? null : atual))
  }

  if (isLoading) {
    return (
      <p className="mt-12 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando dados do DFD…
      </p>
    )
  }
  if (error || !inicial) {
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-dark-card">
        <FontAwesomeIcon icon={faExclamationTriangle} className="mb-4 text-5xl text-amber-400" />
        <h1 className="mb-2 text-2xl font-semibold text-slate-700 dark:text-slate-200">
          Não é possível iniciar o ETP
        </h1>
        <p className="mb-6 text-slate-500">
          {(error as Error)?.message ??
            'É necessário gerar o DFD antes de criar o ETP.'}
        </p>
        <Link to="/processos" className="inline-flex items-center rounded-lg bg-indigo-600 px-6 py-3 font-semibold text-white hover:bg-indigo-700">
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar ao processo
        </Link>
      </div>
    )
  }

  const indispensaveis = new Set(configuracao?.indispensaveis ?? [])
  const secoes = (configuracao?.secoes ?? []).filter((s) => !NAO_COLETADAS.has(s.chave))
  const subsecoes = configuracao?.subsecoes ?? []
  const herdados = Object.entries(inicial.herdado_do_dfd).filter(
    ([chave, valor]) => valor && ROTULOS_HERDADOS[chave],
  )

  function gerar(evento: React.FormEvent) {
    evento.preventDefault()
    if (!inicial) return
    const faltando: Record<string, string> = {}
    for (const chave of indispensaveis) {
      if (NAO_COLETADAS.has(chave)) continue
      if (!valores[chave]?.trim()) faltando[chave] = 'Elemento indispensável (Art. 18 §1º).'
    }
    if (Object.keys(faltando).length > 0) {
      setErros(faltando)
      notificar('error', 'Preencha os elementos indispensáveis destacados.')
      return
    }

    // A geração segue no endpoint legado: form nativo, como no DFD
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/etp/create'
    const anexar = (nome: string, valor: string) => {
      const campo = document.createElement('input')
      campo.type = 'hidden'
      campo.name = nome
      campo.value = valor
      form.appendChild(campo)
    }
    Object.entries(valores).forEach(([nome, valor]) => anexar(nome, valor ?? ''))
    Object.entries(inicial.herdado_do_dfd).forEach(([nome, valor]) =>
      anexar(nome, valor ?? ''),
    )
    anexar('licitacao_id', String(idNumerico))
    const token = document.cookie
      .split('; ')
      .find((c) => c.startsWith('XSRF-TOKEN='))
      ?.slice('XSRF-TOKEN='.length)
    if (token) anexar('csrf_token', decodeURIComponent(token))
    document.body.appendChild(form)
    form.submit()
  }

  const entrada =
    'w-full rounded-lg border border-slate-300 px-4 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100'

  return (
    <form className="mx-auto max-w-5xl" onSubmit={gerar}>
      <header className="mb-8 text-center">
        <h1 className="mb-2 text-3xl font-bold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faFileContract} className="mr-3 text-indigo-600" />
          Estudo Técnico Preliminar
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          Lei 14.133/2021, Art. 18 — os campos abaixo são os insumos; a IA
          desenvolve cada seção a partir deles e do DFD.
        </p>
        {rascunho.isSuccess && (
          <p className="mt-2 text-sm text-emerald-600">
            <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
            Rascunho salvo
          </p>
        )}
      </header>

      <div className="mb-6 flex flex-col gap-3 rounded-lg border border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 p-4 shadow-sm md:flex-row md:items-center md:justify-between dark:border-indigo-800 dark:from-indigo-900/20 dark:to-purple-900/20">
        <p className="text-sm text-slate-700 dark:text-slate-200">
          <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-indigo-600" />
          <strong>Dica:</strong> ao clicar num campo de texto vazio, a LicitIA
          sugere um conteúdo inicial. ESC cancela; o botão de cada seção refaz.
        </p>
        <button
          type="button"
          onClick={() => setSugestoesAtivas((v) => !v)}
          className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors ${
            sugestoesAtivas
              ? 'bg-indigo-600 hover:bg-indigo-700'
              : 'bg-slate-400 hover:bg-slate-500'
          }`}
        >
          <FontAwesomeIcon icon={sugestoesAtivas ? faMagic : faBan} />
          {sugestoesAtivas ? 'Sugestões ativadas' : 'Sugestões desativadas'}
        </button>
      </div>

      <fieldset className="mb-6 rounded-xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <legend className="px-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-indigo-600" />
          Identificação
        </legend>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label htmlFor="objeto_contratacao" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Objeto da Contratação <span className="text-red-500">*</span>
            </label>
            <input
              id="objeto_contratacao"
              name="objeto_contratacao"
              type="text"
              required
              value={valores.objeto_contratacao ?? ''}
              onChange={(e) => definir('objeto_contratacao', e.target.value)}
              className={entrada}
            />
          </div>
          <div>
            <label htmlFor="valor_estimado" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Valor Estimado (R$)
            </label>
            <input
              id="valor_estimado"
              name="valor_estimado"
              type="text"
              value={valores.valor_estimado ?? ''}
              onChange={(e) => definir('valor_estimado', e.target.value)}
              className={entrada}
            />
          </div>
        </div>
      </fieldset>

      {herdados.length > 0 && (
        <fieldset className="mb-6 rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 p-6 dark:border-indigo-800 dark:from-indigo-900/20 dark:to-purple-900/20">
          <legend className="px-2 text-lg font-semibold text-indigo-800 dark:text-indigo-300">
            <FontAwesomeIcon icon={faGavel} className="mr-2" />
            Herdado do DFD
          </legend>
          <p className="mb-3 text-sm text-indigo-700 dark:text-indigo-400">
            Estes campos vêm da demanda já formalizada e seguem para a geração.
            Para alterá-los, edite o DFD.
          </p>
          <dl className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {herdados.map(([chave, valor]) => (
              <div key={chave} className="rounded-lg border border-indigo-100 bg-white p-3 dark:border-indigo-900 dark:bg-dark-card">
                <dt className="text-xs uppercase text-slate-500">{ROTULOS_HERDADOS[chave]}</dt>
                <dd className="text-sm font-semibold text-slate-800 dark:text-slate-100">{valor}</dd>
              </div>
            ))}
          </dl>
        </fieldset>
      )}

      <fieldset className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-6 dark:border-amber-800 dark:bg-amber-900/20">
        <legend className="px-2 text-lg font-semibold text-amber-800 dark:text-amber-300">
          <FontAwesomeIcon icon={faMagnifyingGlassDollar} className="mr-2" />
          Pesquisa de Preços (Art. 23)
        </legend>
        <div className="space-y-3">
          {(configuracao?.fontes_pesquisa_precos ?? []).map((fonte) => (
            <label
              key={fonte.valor}
              className={`flex cursor-pointer gap-3 rounded-lg border-2 p-4 transition-colors ${
                valores.fonte_pesquisa_precos === fonte.valor
                  ? 'border-amber-500 bg-white dark:bg-dark-card'
                  : 'border-transparent bg-white/60 hover:border-amber-300 dark:bg-dark-card/60'
              }`}
            >
              <input
                type="radio"
                name="fonte_pesquisa_precos"
                value={fonte.valor}
                checked={valores.fonte_pesquisa_precos === fonte.valor}
                onChange={(e) => definir('fonte_pesquisa_precos', e.target.value)}
                className="mt-1 h-4 w-4 accent-amber-600"
              />
              <span>
                <span className="block font-semibold text-slate-800 dark:text-slate-100">
                  {fonte.rotulo}
                </span>
                <span className="block text-sm text-slate-600 dark:text-slate-400">
                  {fonte.descricao}
                </span>
              </span>
            </label>
          ))}
        </div>

        {valores.fonte_pesquisa_precos === 'webscraper' && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-white p-4 dark:border-amber-800 dark:bg-dark-card">
            <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
              Termos de busca no PNCP
            </p>
            <div className="mb-3 flex gap-4">
              {[
                { valor: 'auto', rotulo: 'Automático (usa o objeto)' },
                { valor: 'manual', rotulo: 'Informar termos' },
              ].map((modo) => (
                <label key={modo.valor} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <input
                    type="radio"
                    name="webscraper_mode"
                    value={modo.valor}
                    checked={(valores.webscraper_mode ?? 'auto') === modo.valor}
                    onChange={(e) => definir('webscraper_mode', e.target.value)}
                    className="h-4 w-4 accent-amber-600"
                  />
                  {modo.rotulo}
                </label>
              ))}
            </div>
            {valores.webscraper_mode === 'manual' && (
              <>
                <label htmlFor="webscraper_keywords" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Palavras-chave
                </label>
                <input
                  id="webscraper_keywords"
                  name="webscraper_keywords"
                  type="text"
                  value={valores.webscraper_keywords ?? ''}
                  onChange={(e) => definir('webscraper_keywords', e.target.value)}
                  placeholder="Ex.: cimento, areia, brita"
                  className={entrada}
                />
              </>
            )}
            <p className="mt-3 text-xs text-amber-700 dark:text-amber-400">
              <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
              Nesta fonte a geração pausa para você escolher quais contratações
              entram na estimativa.
            </p>
          </div>
        )}
      </fieldset>

      {secoes.map((secao) => {
        const obrigatoria = indispensaveis.has(secao.chave)
        return (
          <fieldset
            key={secao.chave}
            className={`mb-6 rounded-xl border bg-white p-6 shadow-lg dark:bg-dark-card ${
              erros[secao.chave]
                ? 'border-red-400 dark:border-red-600'
                : 'border-slate-200 dark:border-slate-700'
            }`}
          >
            <legend className="px-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
              {secao.titulo}
              {obrigatoria && (
                <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/40 dark:text-red-300">
                  indispensável
                </span>
              )}
            </legend>
            <div className="mb-2 flex justify-end">
              <button
                type="button"
                onClick={() => void sugerir(secao.chave)}
                disabled={sugerindo === secao.chave}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-100 disabled:opacity-60 dark:bg-indigo-900/30 dark:text-indigo-300"
              >
                <FontAwesomeIcon
                  icon={sugerindo === secao.chave ? faSpinner : faWandMagicSparkles}
                  spin={sugerindo === secao.chave}
                />
                Sugestão Inteligente
              </button>
            </div>
            <label htmlFor={secao.chave} className="sr-only">
              {secao.titulo}
            </label>
            <textarea
              id={secao.chave}
              name={secao.chave}
              rows={6}
              value={valores[secao.chave] ?? ''}
              onChange={(e) => definir(secao.chave, e.target.value)}
              onFocus={() => aoFocar(secao.chave)}
              onKeyDown={(e) => aoTeclar(secao.chave, e.key)}
              placeholder={
                sugerindo === secao.chave
                  ? '🤖 LicitIA está gerando a sugestão… (ESC cancela)'
                  : AJUDA[secao.chave]
              }
              className={`${entrada} ${
                sugerindo === secao.chave
                  ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20'
                  : ''
              }`}
            />
            {erros[secao.chave] && (
              <p role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">
                {erros[secao.chave]}
              </p>
            )}
          </fieldset>
        )
      })}

      <fieldset className="mb-6 rounded-xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <legend className="px-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-indigo-600" />
          Subseções
        </legend>
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
          Detalhamentos que a IA desenvolve dentro das seções correspondentes.
        </p>
        {subsecoes.map((sub) => (
          <div key={sub.chave} className="mb-4">
            <div className="mb-1 flex items-center justify-between">
              <label htmlFor={sub.chave} className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {sub.titulo}
              </label>
              <button
                type="button"
                onClick={() => void sugerir(sub.chave)}
                disabled={sugerindo === sub.chave}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-100 disabled:opacity-60 dark:bg-indigo-900/30 dark:text-indigo-300"
              >
                <FontAwesomeIcon
                  icon={sugerindo === sub.chave ? faSpinner : faWandMagicSparkles}
                  spin={sugerindo === sub.chave}
                />
                Sugestão
              </button>
            </div>
            <textarea
              id={sub.chave}
              name={sub.chave}
              rows={4}
              value={valores[sub.chave] ?? ''}
              onChange={(e) => definir(sub.chave, e.target.value)}
              onFocus={() => aoFocar(sub.chave)}
              onKeyDown={(e) => aoTeclar(sub.chave, e.key)}
              placeholder={
                sugerindo === sub.chave
                  ? '🤖 LicitIA está gerando a sugestão… (ESC cancela)'
                  : AJUDA[sub.chave]
              }
              className={`${entrada} ${
                sugerindo === sub.chave
                  ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20'
                  : ''
              }`}
            />
          </div>
        ))}
      </fieldset>

      <div className="flex flex-col justify-between gap-3 border-t border-slate-200 pt-6 pb-4 md:flex-row dark:border-slate-700">
        <Link to="/processos" className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-200 px-6 py-3 font-semibold text-slate-700 transition-colors hover:bg-slate-300 dark:bg-dark-hover dark:text-slate-200">
          <FontAwesomeIcon icon={faArrowLeft} />
          Voltar
        </Link>
        <div className="flex flex-col gap-3 md:flex-row">
          <button
            type="button"
            onClick={() => rascunho.mutate(valores)}
            disabled={rascunho.isPending}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={rascunho.isPending ? faSpinner : faSave} spin={rascunho.isPending} />
            Salvar Rascunho
          </button>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-indigo-700 hover:to-purple-700"
          >
            <FontAwesomeIcon icon={faWandMagicSparkles} />
            Gerar ETP Com LicitIA
          </button>
        </div>
      </div>
    </form>
  )
}
